'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

interface ScrollTopProviderProps {
  children: React.ReactNode;
}

/**
 * 페이지 전환 시 스크롤 위치를 관리하는 Provider
 * 다음 우선순위로 스크롤 위치를 결정합니다:
 * 1. URL 해시(#)와 일치하는 ID를 가진 요소
 * 2. data-scroll-target 속성을 가진 요소
 * 3. 위 조건이 없는 경우 페이지 최상단으로 스크롤
 */

const SCROLL_TARGET_ATTRIBUTE = 'data-scroll-target';

const ScrollNavigationProvider = ({ children }: ScrollTopProviderProps) => {
  // 링크 클릭 감지 및 스크롤 타겟 추적
  const { hashRef } = useNavigationTracker();
  // 페이지 전환 시 스크롤 처리
  useScrollEffect(hashRef);
  return children;
};

/**
 * 링크 클릭을 감지하여 해시 정보를 관리하는 훅
 */
const useNavigationTracker = () => {
  const hashRef = useRef<string | null>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');

      // 링크가 없으면 처리하지 않음
      if (!link) return;

      try {
        const url = new URL(link.href);

        // 1. 해시 처리
        if (link.href.includes('#')) {
          hashRef.current = url.hash.substring(1);
          return;
        }
      } catch (error) {
        console.warn('useNavigationTracker 참고', error);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return { hashRef };
};

/**
 * 페이지 전환 시 스크롤 처리를 담당하는 훅
 */
const useScrollEffect = (hashRef: React.RefObject<string | null>) => {
  const pathname = usePathname();
  useEffect(() => {
    // 1. 저장된 해시가 있으면 해당 요소로 스크롤
    if (hashRef.current) {
      const element = document.getElementById(hashRef.current);
      if (element) {
        element.scrollIntoView();
        hashRef.current = null;
        return;
      }
    }
    // 2. data-scroll-target 속성이 있는 요소로 스크롤
    const element = document.querySelector(`[${SCROLL_TARGET_ATTRIBUTE}]`);
    if (element) {
      element.scrollIntoView();
      return;
    }
    // 3. 그 외에는 최상단으로 스크롤
    window.scrollTo(0, 0);
  }, [pathname, hashRef]);
};

export default ScrollNavigationProvider;
